// src/components/RichText.js
// -----------------------------------------------------------------------------
// DBOS native rich-text editor. No third-party dependency.
//
// Product descriptions are stored as HTML (merchants write formatted copy, and every
// migrated catalogue - Shopify, Woo, WordPress - arrives as HTML). The storefront
// renders that HTML; this is where it is authored. A raw-markup textarea is not
// something a shop owner should ever see.
//
// CONSTRAINED BY DESIGN. Only a small tag set survives: p, br, strong, em, u, ul, ol,
// li, a[href]. Everything else - style attributes, classes, divs, scripts, iframes,
// event handlers - is stripped, on paste AND on read. That is a safety property, not
// tidiness: the storefront renders this with dangerouslySetInnerHTML, so whatever this
// component allows is what can reach a customer's browser. A smaller allow-list is a
// smaller attack surface, and it is why a full-featured editor library would be the
// wrong choice here even setting its weight aside.
//
// Existing merchant markup (e.g. Shopify's <div class="flex gap-2 ..."> blocks) renders
// as readable content and is normalised to this tag set when the product is next saved.
// Content is preserved; layout markup is not. Catalogues normalise product by product
// as they are edited - no bulk rewrite, no surprise migration.
//
// execCommand note: formally deprecated, implemented everywhere, and the only
// dependency-free way to do this. Behaviour is confined to the six commands below, so
// a future replacement is a contained change.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------
import React, { useRef, useEffect, useCallback } from 'react';

const ALLOWED = {
  P: [], BR: [], STRONG: [], EM: [], U: [], UL: [], OL: [], LI: [], A: ['href'],
};
// tags whose CONTENT we keep but whose wrapper we drop (Shopify/Woo layout soup)
const UNWRAP = new Set(['DIV', 'SPAN', 'SECTION', 'ARTICLE', 'FONT', 'TABLE',
  'TBODY', 'TR', 'TD', 'TH', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'B', 'I']);
// tags removed entirely, content and all
const DROP = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM',
  'INPUT', 'BUTTON', 'SVG', 'LINK', 'META', 'NOSCRIPT', 'IMG', 'VIDEO']);

// B/I are mapped rather than unwrapped - they carry meaning the editor emits
const REMAP = { B: 'STRONG', I: 'EM' };

function sanitizeNode(node, out) {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      out.appendChild(document.createTextNode(child.textContent));
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) continue;

    const tag = child.tagName;
    if (DROP.has(tag)) continue;

    const mapped = REMAP[tag] || tag;

    if (ALLOWED[mapped]) {
      const el = document.createElement(mapped.toLowerCase());
      for (const attr of ALLOWED[mapped]) {
        const v = child.getAttribute(attr);
        // only http(s) and mailto links - no javascript: payloads
        if (attr === 'href' && v && !/^(https?:|mailto:)/i.test(v)) continue;
        if (v) el.setAttribute(attr, v);
      }
      sanitizeNode(child, el);
      out.appendChild(el);
      continue;
    }

    if (UNWRAP.has(tag)) {
      // keep the text, drop the wrapper, but preserve the block break
      const frag = document.createElement('p');
      sanitizeNode(child, frag);
      if (frag.textContent.trim()) out.appendChild(frag);
      continue;
    }

    // unknown tag: keep its content, discard the tag
    sanitizeNode(child, out);
  }
}

export function sanitizeHtml(html) {
  if (!html) return '';
  const src = document.createElement('div');
  src.innerHTML = html;
  const out = document.createElement('div');
  sanitizeNode(src, out);
  // collapse empty paragraphs the unwrapping can leave behind
  Array.from(out.querySelectorAll('p')).forEach((p) => {
    if (!p.textContent.trim() && !p.querySelector('br')) p.remove();
  });
  return out.innerHTML;
}

const S = {
  wrap: { border: '1px solid rgba(255,255,255,0.1)', background: '#1a1d1a' },
  bar: {
    display: 'flex', gap: 4, padding: '6px 8px',
    borderBottom: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap',
  },
  btn: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.75)', fontSize: 11, fontWeight: 700, cursor: 'pointer',
    padding: '4px 9px', lineHeight: 1.4, minWidth: 28,
  },
  area: {
    color: '#e8e6df', padding: '9px 12px', fontSize: 12, fontFamily: 'inherit',
    outline: 'none', minHeight: 110, lineHeight: 1.6, overflowY: 'auto',
    maxHeight: 320,
  },
};

export default function RichText({ value, onChange, placeholder }) {
  const ref = useRef(null);
  const last = useRef('');

  // Load incoming HTML only when it differs from what we last emitted. Without this
  // guard every keystroke would rewrite innerHTML and throw the caret to the start.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const incoming = value || '';
    if (incoming !== last.current) {
      el.innerHTML = sanitizeHtml(incoming);
      last.current = incoming;
    }
  }, [value]);

  const emit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const html = sanitizeHtml(el.innerHTML);
    last.current = html;
    onChange(html);
  }, [onChange]);

  const cmd = (command, arg) => {
    document.execCommand(command, false, arg);
    ref.current?.focus();
    emit();
  };

  const onPaste = (e) => {
    // Pasting from Word or a brand site is how class-soup enters a catalogue in the
    // first place. Take the markup, sanitise it, insert that.
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (html) {
      document.execCommand('insertHTML', false, sanitizeHtml(html));
    } else {
      document.execCommand('insertText', false, text);
    }
    emit();
  };

  const link = () => {
    const url = window.prompt('Link URL (https://...)');
    if (!url) return;
    if (!/^(https?:|mailto:)/i.test(url)) {
      window.alert('Only https:// and mailto: links are allowed.');
      return;
    }
    cmd('createLink', url);
  };

  return (
    <div style={S.wrap}>
      <div style={S.bar}>
        <button type="button" style={{ ...S.btn, fontWeight: 900 }} title="Bold"
          onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('bold')}>B</button>
        <button type="button" style={{ ...S.btn, fontStyle: 'italic' }} title="Italic"
          onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('italic')}>I</button>
        <button type="button" style={S.btn} title="Bullet list"
          onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('insertUnorderedList')}>• List</button>
        <button type="button" style={S.btn} title="Numbered list"
          onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('insertOrderedList')}>1. List</button>
        <button type="button" style={S.btn} title="Add a link"
          onMouseDown={(e) => e.preventDefault()} onClick={link}>Link</button>
        <button type="button" style={S.btn} title="Remove formatting"
          onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('removeFormat')}>Clear</button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        style={S.area}
        data-placeholder={placeholder || ''}
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
      />
    </div>
  );
}